
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { getSliderTrackStyles } from './SliderStyles';

/**
 * This interface expresses where the two different subbars (the range indicator and data bar) should be displayed.
 * The positions are proportions (i.e. 0.5) that are translated to percentages (50%) (relative to the whole horizontal
 * width of the track).
 */
export interface SliderPositionRange {
  start: number;
  end?: number;
}

export interface SliderTrackProps {
  /** The data bar indicates the area of the slider that 'has data'.  It's a very 'Tableau' feature. */
  dataBarPosition?: SliderPositionRange;

  disabled?: boolean;

  /** Indicates if a handle is being dragged. */
  handleIsBeingDragged?: boolean;

  /** Indicates if the range handle has focus. Used for applying focus styling to the range portion of the slider track. */
  isRangeHandleFocused?: boolean;

  /**
   * This bar indicates the area of the slider that, considering the current value of the slider handles, is
   * 'in range'.  E.g. for a two handled slider this would be the area between the handles.  For an open ended
   * slider, to the left, it would be everything from the handle to the left end.
   */
  rangeIndicatorBarPosition?: SliderPositionRange;

  /**
   * Test id to be attached to the slider and its contents.
   * The suffix '-SliderTrack' will be automatically added
   */
  testId: string;
}

/**
 * The slider track appears underneath the slider handles in the slider widget.  This component itself is not
 * interactive, but the track can seem like its interactive since the SliderHandleContainer is positioned on top of
 * it and does let you click anywhere in the track to grab the handles.
 */
export function SliderTrackWidget(props: SliderTrackProps): JSX.Element {
  const styles = getSliderTrackStyles();

  let indicatorBar;
  if (props.rangeIndicatorBarPosition) {
    const leftPositionAsPercentage = props.rangeIndicatorBarPosition.start * 100;
    const rightPositionAsPercentage = (1 - props.rangeIndicatorBarPosition.end!) * 100;
    const style = props.disabled ? styles.RangeIndicatorBarDisabled :
      (props.isRangeHandleFocused && !props.handleIsBeingDragged ? styles.RangeIndicatorBarFocused : styles.RangeIndicatorBar);
    indicatorBar = <div data-tb-test-id={makeTestId(props.testId, 'IndicatorBar')}
      style={{ ...style, left: `${leftPositionAsPercentage}%`, right: `${rightPositionAsPercentage}%` }}></div>;
  }

  let dataBar;
  if (props.dataBarPosition) {
    const leftPositionAsPercentage = props.dataBarPosition.start * 100;
    const rightPositionAsPercentage = (1 - props.dataBarPosition.end!) * 100;
    const style = props.disabled ? styles.DataBarDisabled :
      (props.isRangeHandleFocused && !props.handleIsBeingDragged ? styles.DataBarFocused : styles.DataBar);
    dataBar = <div data-tb-test-id={makeTestId(props.testId, 'DataBar')}
      style={{ ...style, left: `${leftPositionAsPercentage}%`, right: `${rightPositionAsPercentage}%` }}></div>;
  }

  return (
    <div style={styles.Track}
      data-tb-test-id={makeTestId(props.testId, 'SliderTrack')}>
      {indicatorBar}
      {dataBar}
    </div>
  );
}
